import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  ListChecks,
  CreditCard,
  PenSquare,
  Settings2,
  Cog,
  LogOut,
  ShieldCheck,
  Mail,
  Menu,
  X,
  TrendingUp,
  Tag,
  Building2,
  Scale,
  Eraser,
  Home,
} from "lucide-react";
import { Button } from "@/components/ui/button";

const links = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/revenue", label: "Omzet", icon: TrendingUp },
  { to: "/admin/analyses", label: "Analyses", icon: ListChecks },
  { to: "/admin/payments", label: "Betalingen", icon: CreditCard },
  { to: "/admin/businesses", label: "Bedrijven", icon: Building2 },
  { to: "/admin/discount-codes", label: "Kortingscodes", icon: Tag },
  { to: "/admin/homepage", label: "Homepagina", icon: Home },
  { to: "/admin/blog", label: "Blog", icon: PenSquare },
  { to: "/admin/emails", label: "E-mails", icon: Mail },
  { to: "/admin/legal-pages", label: "Juridisch", icon: Scale },
  { to: "/admin/gdpr", label: "GDPR cleanup", icon: Eraser },
  { to: "/admin/config", label: "Configuratie", icon: Cog },
  { to: "/admin/settings", label: "Instellingen", icon: Settings2 },
];

export default function AdminLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [authed, setAuthed] = useState(() => Boolean(localStorage.getItem("oc_admin_token")));

  useEffect(() => {
    if (!localStorage.getItem("oc_admin_token")) {
      navigate("/admin/login", { replace: true });
      setAuthed(false);
    } else {
      setAuthed(true);
    }
  }, [navigate, location.pathname]);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const logout = () => {
    localStorage.removeItem("oc_admin_token");
    localStorage.removeItem("oc_admin_email");
    navigate("/admin/login", { replace: true });
  };

  const adminEmail = typeof window !== "undefined" ? localStorage.getItem("oc_admin_email") : "";

  const Sidebar = ({ mobile = false }) => (
    <aside
      data-testid="admin-sidebar"
      className={`w-64 bg-[#003B82] text-white flex flex-col p-6 ${
        mobile ? "h-full" : "sticky top-0 h-screen hidden lg:flex"
      }`}
    >
      <div className="flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 font-display font-bold text-lg text-white">
          <ShieldCheck className="w-6 h-6 text-white/90" />
          <span className="text-white">OfferteExpert</span>
        </Link>
        {mobile && (
          <button onClick={() => setMobileOpen(false)} aria-label="Sluit menu" className="text-white/85 p-1">
            <X className="w-5 h-5" />
          </button>
        )}
      </div>
      <p className="text-white/55 text-[12px] uppercase tracking-[0.1em] mt-1">Admin</p>

      <nav className="mt-10 flex flex-col gap-1.5">
        {links.map(({ to, label, icon: Icon, end }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            data-testid={`admin-nav-${label.toLowerCase()}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3.5 py-2.5 rounded-oc-btn text-[14px] font-medium transition-colors ${
                isActive ? "bg-white/10 text-white" : "text-white/65 hover:bg-white/5 hover:text-white"
              }`
            }
          >
            <Icon className="w-4 h-4" />
            {label}
          </NavLink>
        ))}
      </nav>

      <div className="mt-auto pt-6">
        <p className="text-white/55 text-[12px] truncate">{adminEmail}</p>
        <Button
          data-testid="admin-logout"
          variant="ghost"
          onClick={logout}
          className="mt-2 w-full justify-start text-white/85 hover:bg-white/10 hover:text-white rounded-oc-btn"
        >
          <LogOut className="w-4 h-4 mr-2" /> Uitloggen
        </Button>
      </div>
    </aside>
  );

  return (
    <div className="min-h-screen flex bg-oc-bg">
      {!authed ? null : <>
      <Sidebar />

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileOpen(false)} />
          <div className="absolute left-0 top-0 h-full">
            <Sidebar mobile />
          </div>
        </div>
      )}

      <div className="flex-1 min-w-0">
        {/* Mobile top bar */}
        <header className="lg:hidden sticky top-0 z-30 bg-white border-b border-oc-border flex items-center justify-between px-4 h-14">
          <button onClick={() => setMobileOpen(true)} className="text-oc-primary p-2" aria-label="Open menu">
            <Menu className="w-5 h-5" />
          </button>
          <span className="font-display font-bold text-oc-primary text-[15px]">OfferteExpert Admin</span>
          <button onClick={logout} className="text-oc-textSecondary p-2" aria-label="Uitloggen">
            <LogOut className="w-4 h-4" />
          </button>
        </header>

        <main className="p-5 sm:p-7 lg:p-12 overflow-x-hidden">
          <Outlet />
        </main>
      </div>
      </>}
    </div>
  );
}
